import { useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Lock, CheckCircle, AlertCircle, ArrowLeft, Loader2 } from "lucide-react";
import { BACKEND_URL } from "../context/AuthContext";
import Header from "../components/Header";
import SEO from "../components/SEO";

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) { setError("Password must be at least 6 characters."); return; }
    if (password !== confirm) { setError("Passwords don't match."); return; }
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, password }),
      });
      const data = await res.json();
      if (res.ok) {
        setSuccess(true);
      } else {
        setError(data.detail || "Reset failed. Please try again.");
      }
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] text-white">
        <SEO title="Reset Password" noindex={true} />
        <Header />
        <div className="flex items-center justify-center min-h-[80vh] px-6">
          <div className="text-center max-w-md">
            <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h1 className="text-2xl font-bold mb-2" data-testid="reset-no-token">Invalid Reset Link</h1>
            <p className="text-white/50 mb-6">No reset token found. Please request a new password reset.</p>
            <button onClick={() => navigate("/login")} className="bg-purple-500 hover:bg-purple-600 text-white px-6 py-3 rounded-xl font-semibold transition" data-testid="reset-go-login">
              Go to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] text-white">
        <SEO title="Password Reset" noindex={true} />
        <Header />
        <div className="flex items-center justify-center min-h-[80vh] px-6">
          <div className="text-center max-w-md">
            <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-400" />
            </div>
            <h1 className="text-2xl font-bold mb-2" data-testid="reset-success">Password Reset!</h1>
            <p className="text-white/50 mb-6">Your password has been updated. You can now log in with your new password.</p>
            <button onClick={() => navigate("/login")} className="bg-purple-500 hover:bg-purple-600 text-white px-6 py-3 rounded-xl font-semibold transition" data-testid="reset-go-login-success">
              Go to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      <SEO title="Reset Password" noindex={true} />
      <Header />
      <div className="flex items-center justify-center min-h-[80vh] px-6">
        <div className="w-full max-w-md">
          <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-purple-500/20 rounded-xl flex items-center justify-center">
                <Lock className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <h1 className="text-xl font-bold" data-testid="reset-heading">Set New Password</h1>
                <p className="text-white/40 text-sm">Choose a strong password for your account.</p>
              </div>
            </div>

            {error && (
              <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-xl mb-4 text-sm" data-testid="reset-error">
                <AlertCircle className="w-4 h-4 flex-shrink-0" /> {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="text-white/60 text-sm mb-1.5 block">New Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-purple-500 transition text-sm"
                  placeholder="At least 6 characters"
                  data-testid="reset-password-input"
                />
              </div>
              <div>
                <label className="text-white/60 text-sm mb-1.5 block">Confirm Password</label>
                <input
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-purple-500 transition text-sm"
                  placeholder="Type your password again"
                  data-testid="reset-confirm-input"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-purple-500 hover:bg-purple-600 disabled:opacity-50 text-white font-semibold py-3 rounded-xl transition flex items-center justify-center gap-2"
                data-testid="reset-submit-btn"
              >
                {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Resetting...</> : "Reset Password"}
              </button>
            </form>

            <button onClick={() => navigate("/login")} className="flex items-center gap-1.5 text-white/40 hover:text-white/70 text-sm transition mx-auto mt-5" data-testid="reset-back-login">
              <ArrowLeft className="w-3.5 h-3.5" /> Back to Login
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
